/**
 * 비로그인 사용자가 무료 체험 CTA를 누른 뒤 회원가입을 완료하면,
 * 이 컴포넌트가 자동으로 7일 무료 체험을 시작한다.
 *
 * 위치: <ToastProvider> 자식 영역 (App.js)
 *   - AuthContext가 더 상위에 있어 currentUser 변경을 안전하게 감지할 수 있음
 *   - ToastProvider 자식이므로 useToast() 사용 가능
 *
 * 동작:
 *   1. 마운트 시 + currentUser 변경 시 localStorage pending 키 확인
 *   2. 키가 있고 currentUser가 존재하면 1회만 /api/payment/subscription/free-trial 호출
 *   3. 사용자 문서 초기화 뒤 무료 체험을 시작하고 성공할 때만 pending 삭제
 *   4. 구독 상태를 갱신한 뒤 원래 화면으로 복귀
 *
 * 안전장치:
 *   - 같은 세션에서 한 번 실행했으면 sessionStorage 플래그로 재실행 방지
 *   - pending 키는 10분 만료, 만료된 경우 자동 무시
 *   - 호출 실패 시 토스트 없이 조용히 종료 (이미 사용 등 정상 케이스 포함)
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../Toast';
import {
  hasPendingFreeTrial,
  SESSION_AUTO_DONE_FLAG,
  trackPendingFreeTrialMetaPixel,
  triggerPendingFreeTrialNow,
} from '../../utils/freeTrialPending';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { trackFreeTrialStart } from '../../utils/eventTracker';
import { getMetaMatchParams } from '../../utils/analytics';
import { saveClassPhotoTrialWelcome } from '../../utils/classPhotoTrialWelcome';

const SESSION_DONE_FLAG = SESSION_AUTO_DONE_FLAG;

const FreeTrialAutoStarter = () => {
  const { currentUser } = useAuth();
  const { fetchSubscriptionStatus } = useSubscription();
  const { showToast } = useToast();
  const location = useLocation();
  const runningRef = useRef(false);

  useEffect(() => {
    if (!currentUser) return;
    if (runningRef.current) return;
    if (sessionStorage.getItem(SESSION_DONE_FLAG)) return;
    if (location.pathname === '/auth') return;
    if (!hasPendingFreeTrial()) return;

    // 자동 트리거 게이트
    // - 이메일 회원가입 후 메일 인증 전(emailVerified=false): 인증되지 않은 계정에 PRO
    //   권한 부여는 부적절. 인증 완료 시 onAuthStateChanged가 새 user 객체로 다시 발화하여
    //   이 useEffect가 재실행되므로 자연스럽게 처리됨.
    // - Google 로그인은 emailVerified=true가 보장되어 통과.
    // - 토스 심사용 임시 사용자(temp_toss_user): emailVerified가 undefined이고 isTempReviewUser
    //   플래그가 붙어 있음 → 명시적으로 skip하여 Firebase 토큰 없는 객체로 API 호출하는
    //   비정상 동작 차단.
    if (!currentUser.emailVerified) return;
    if (currentUser.isTempReviewUser) return;

    runningRef.current = true;

    const run = async () => {
      try {
        let result = null;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          result = await triggerPendingFreeTrialNow(currentUser, {
            getApiBaseUrl,
            getMetaMatchParams,
          });
          if (result.success || !result.retryable) break;
          await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
        }

        if (result?.success) {
          trackFreeTrialStart(result.triggerFeature);
          trackPendingFreeTrialMetaPixel(result);
          await fetchSubscriptionStatus(true);
          saveClassPhotoTrialWelcome({
            triggerFeature: result.triggerFeature,
            redirectPath: result.triggerPage,
          });
          showToast('7일 PRO 무료 체험이 시작됐어요.', 'success', 6000);
          window.location.replace(result.triggerPage || window.location.href);
        }
      } catch (err) {
        console.warn('회원가입 후 자동 체험 시작 실패:', err);
      } finally {
        runningRef.current = false;
      }
    };

    run();
  }, [
    currentUser,
    fetchSubscriptionStatus,
    location.pathname,
    showToast,
  ]);

  return null;
};

export default FreeTrialAutoStarter;
